/**
 * The running commentary — "Mila pakt de slag", "Ronde 3: 5 kaarten" — as a live
 * region, so a screen reader hears the match instead of nothing at all.
 *
 * One component for all three games. They had three identical lists, and this is
 * the kind of thing that gets fixed in one of the three and stays broken in the
 * other two.
 *
 * Two details that are the whole point, and both are easy to undo by accident:
 *
 * 1. **The region is always mounted**, even with nothing in it. A live region
 *    that appears together with its first message is usually not announced at
 *    all: readers watch an existing region for changes, and a brand-new node
 *    carrying text is just new content. So the `<ul>` renders unconditionally and
 *    only the items come and go.
 *
 * 2. **`aria-atomic="false"`**, because `role="status"` implies atomic *true* and
 *    that would re-read every notice on screen each time one is added. This feed
 *    only ever appends; announcing the addition is exactly right.
 */

export interface Notice {
  id: number;
  text: string;
}

export default function Notices({
  items,
  limit,
  show = true,
}: {
  items: ReadonlyArray<Notice>;
  /** How many of the most recent to show. The rest stay in the store. */
  limit?: number;
  /**
   * Whether the notices belong on screen at all — false in a lobby, where the
   * player list already says who is there.
   *
   * The region itself stays mounted either way; it is the items that go. A reader
   * hears exactly what a sighted player sees, which is the point of parity.
   */
  show?: boolean;
}) {
  const visible = show ? (limit === undefined ? items : items.slice(-limit)) : [];

  return (
    <ul
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="space-y-1 text-center text-sm text-slate-500"
    >
      {visible.map((notice) => (
        <li key={notice.id}>{notice.text}</li>
      ))}
    </ul>
  );
}
